import { PresentationStudyCardInfo } from './../../../models/search-result/PresentationStudyCardInfo';
import { IDuration, ITuitionFee, PortalType, IReviewRating } from '@studyportals/domain-client';
import { computed, Ref, ref } from 'vue';
import Tracking from '../../../infrastructure/tracking/Tracking';
import { AttendanceFilterOptionValue, FormatFilterOptionValue } from '@studyportals/search-filters/server-side';
import getters from '@/study-search/store/getters';
import mutations from '@/study-search/store/mutations';
import { TimeUnit } from '@/study-search/enums';
import StudyCardDefinitions from '../../../models/study-card/StudyCardDefinitions';
import { LocalContent, LocalizationTargetType } from '@studyportals/portal-localized-content';
import globals from '../../../utils/globals';
import TuitionFeeDiscountCalculator from '../../../models/tuition-fee-discount/TuitionFeeDiscountCalculator';
import { ITuitionFeeDiscountCalculator } from '../../../interfaces';
import { TuitionFeePresentation } from './TuitionFeePresentation';
import { Deployment } from '@/study-search/utils/Deployment';
import { deduplicatedLogger } from '@/study-search/infrastructure/DeduplicatedErrorLogger';
import { DiscountInformation } from '@studyportals/iu-global-access-initiative/interfaces/DiscountInformation';
import IUVisibilityHelper from '@/study-search/models/iu/IUVisibilityHelper';
import { CountryTier } from '@studyportals/iu-global-access-initiative/definitions/CountryTier';
import { ContextualTooltip } from '@studyportals/tooltip';
import { Mobile } from '../../../infrastructure/Mobile';

export default class StudyCard {
	public rankingPercentageRef: Ref<HTMLElement | undefined>;

	public isMasters = computed((): boolean => {
		return getters.getPortalType() === PortalType.MASTER;
	});

	public userCurrency = computed((): string => {
		return getters.getCurrency();
	});

	public userCountry = computed((): string | undefined => {
		return getters.getUserCountryIso();
	});

	public portalType = computed((): string => {
		return getters.getPortalType();
	});

	public deliveryMethodCriteria = computed((): string => {
		const card = this.presentationStudyCardInfo.value?.card;
		return card != null ? this.tuitionFeeDiscountCalculator.getDeliveryMethodCriteria(card) : '';
	});

	public creditsCriteria = computed((): number | undefined => {
		const card = this.presentationStudyCardInfo.value?.card;
		return card != null ? this.tuitionFeeDiscountCalculator.getCreditsCriteria(card) : undefined;
	});

	public degreeTypeCriteria = computed((): string | undefined => {
		const card = this.presentationStudyCardInfo.value?.card;
		return card != null ? this.tuitionFeeDiscountCalculator.getDegreeTypeCriteria(card) : undefined;
	});

	public duration = computed((): IDuration => {
		return this.presentationStudyCardInfo.value?.card.getDuration?.();
	});

	public getDuration = computed((): string | null => {
		if (!this.presentationStudyCardInfo.value?.card.canShowDuration?.()) return null;

		const amount = this.duration.value.getAmount();
		const period = this.duration.value.getPeriod();

		let finalDuration = `${amount} ${period}`;

		if (period === 'months') {
			finalDuration = this.convertMonthlyDuration(amount);
		}
		return finalDuration;
	});

	public getLevel = computed((): string => {
		return this.presentationStudyCardInfo.value?.card.getLevel?.();
	});

	public organisationLogo = computed((): string => {
		return this.presentationStudyCardInfo.value?.card.getLogo?.().getSource?.();
	});

	public organisationName = computed((): string => {
		return this.presentationStudyCardInfo.value?.card.getUniversityLink?.().getDescription?.();
	});

	public organisationId = computed((): number => {
		return this.presentationStudyCardInfo.value?.card.getUniversityId?.();
	});

	public cardId = computed((): number => {
		return this.presentationStudyCardInfo.value?.card.getId?.();
	});

	public canShowLogo = computed((): boolean => {
		return this.enhanced.value && this.presentationStudyCardInfo.value?.card.canShowLogo?.();
	});

	public getDegree = computed((): string => {
		return this.presentationStudyCardInfo.value?.card.getDegree?.();
	});

	public getSummary = computed((): string => {
		return this.presentationStudyCardInfo.value?.card.getSummary?.();
	});

	public getTitle = computed((): string => {
		const title = this.presentationStudyCardInfo.value?.card.getTitle?.();
		return title ? title.replace(/&amp;/g, '&') : '';
	});

	public getLocation = computed((): string => {
		return this.presentationStudyCardInfo.value?.card.getLocationText?.() ?? '';
	});

	public getOrganisationName = computed((): string => {
		return this.presentationStudyCardInfo.value?.card.getUniversityLink?.().getDescription?.();
	});

	public order = computed((): number => {
		return this.presentationStudyCardInfo.value?.order;
	});

	public getProgrammeLink = computed((): string => {
		return `${this.presentationStudyCardInfo.value?.card.getProgrammeLink?.().getUrl?.()}?ref=search_card`;
	});

	public getAttendanceMethods = computed((): string => {
		const isFullTime = this.presentationStudyCardInfo.value?.card.isFullTime?.();
		const isPartTime = this.presentationStudyCardInfo.value?.card.isPartTime?.();

		const fullTimeText = StudyCardDefinitions.format.get(FormatFilterOptionValue.FULL_TIME) ?? '';
		const partTimeText = StudyCardDefinitions.format.get(FormatFilterOptionValue.PART_TIME) ?? '';

		let result = '';

		if (isFullTime) {
			result = fullTimeText || '';
		}
		if (isPartTime) {
			result = partTimeText || '';
		}
		if (isPartTime && isFullTime) {
			result = `${fullTimeText}, ${partTimeText}`;
		}

		return result;
	});

	public canShowIUDiscountInfo = computed((): boolean => {
		return this.presentationStudyCardInfo.value?.canShowIUDiscountInfo;
	});

	public canShowIUIncentive = computed((): boolean => {
		return IUVisibilityHelper.canShowIUIncentive(
			this.tuitionDiscountInformation.value?.countryTier || CountryTier.INELIGIBLE,
			getters.getPortalType(),
			this.presentationStudyCardInfo.value,
		);
	});

	public enhanced = computed((): boolean => {
		return this.presentationStudyCardInfo.value?.enhanced;
	});

	public getDeliveryMethods = computed((): string => {
		const keys: AttendanceFilterOptionValue[] = [];
		if (this.presentationStudyCardInfo.value?.card.isOnline?.()) {
			keys.push(AttendanceFilterOptionValue.ONLINE);
		}
		if (this.presentationStudyCardInfo.value?.card.isOnCampus?.()) {
			keys.push(AttendanceFilterOptionValue.FACE_2_FACE);
		}
		if (this.presentationStudyCardInfo.value?.card.isBlended?.()) {
			keys.push(AttendanceFilterOptionValue.BLENDED);
		}

		return keys.map((_) => StudyCardDefinitions.attendance.get(_)).join(', ');
	});

	public tuitionFee = computed((): string | null => {
		return this.tuitionFeeInfo.value?.amount || null;
	});

	public tuitionUnit = computed((): string | null => {
		return this.tuitionFeeInfo.value?.unit || null;
	});

	public tuitionFeeDiscount = computed((): string | null => {
		return this.tuitionFeeInfo.value?.discountedAmount || null;
	});

	public currencyUnit = computed((): string | null => {
		return this.tuitionFeeInfo.value?.currency || null;
	});

	public noDiscountedAmount = computed((): boolean => {
		return this.tuitionFeeInfo.value?.discountedAmount === null;
	});

	public tuitionDiscountInformation = computed((): DiscountInformation => {
		return this.tuitionFeeDiscountCalculator.getDiscountInformation(
			this.presentationStudyCardInfo.value.card,
			this.getLevel.value,
			this.canShowIUDiscountInfo.value,
		);
	});

	private getReviewRating(): IReviewRating | null {
		try {
			return this.presentationStudyCardInfo.value?.card?.getReviewRating?.();
		} catch {
			return null;
		}
	}

	public averageStarRating = computed((): number => {
		const reviewRating = this.getReviewRating();
		return reviewRating?.getAverageStarRating?.() || 0;
	});

	public reviewQuantity = computed((): number => {
		const reviewRating = this.getReviewRating();
		return reviewRating?.getQuantity?.() || 0;
	});

	public showReviewRating = computed((): boolean => {
		const reviewRating = this.getReviewRating();
		return reviewRating?.hasEnoughQuantity?.() || false;
	});

	public canShowUniversityGlobalPositionPercentage = computed((): boolean => {
		if (Deployment.hasDomainApiMetaRankingPercentageMember(this.presentationStudyCardInfo.value?.card) === false) {
			return false;
		}
		const percentage = this.presentationStudyCardInfo.value?.card.getUniversityGlobalPositionPercentage();
		return percentage !== null && percentage !== '';
	});

	public universityGlobalPositionPercentage = computed((): string => {
		if (!this.canShowUniversityGlobalPositionPercentage.value) {
			return '';
		}
		return this.presentationStudyCardInfo.value?.card.getUniversityGlobalPositionPercentage() as string;
	});

	private impressionTracked = false;
	private tuitionFeeDiscountCalculator: ITuitionFeeDiscountCalculator;

	public tuitionFeeInfo: Ref<TuitionFeePresentation | null> = ref(null);

	constructor(
		public presentationStudyCardInfo: Ref<PresentationStudyCardInfo>,
	) {
		this.rankingPercentageRef = ref();

		this.tuitionFeeDiscountCalculator = new TuitionFeeDiscountCalculator();
	}

	public async mounted(): Promise<void> {
		await this.getTuitionFee().catch((e) => deduplicatedLogger.logDeduplicatedError(e as string));
		const element = document.querySelector('.Featured[data-study-id="' + this.presentationStudyCardInfo.value?.id + '"]');
		if (!element) {
			return;
		}

		this.applyTooltip();
	}

	private applyTooltip(): void {
		const tooltipElement = this.rankingPercentageRef.value;
		const isMobile = Mobile.instance.isMobile.value;
		const percentage = this.presentationStudyCardInfo.value?.card.getUniversityGlobalPositionPercentage();

		if (!tooltipElement || isMobile || !percentage) {
			return;
		}

		// eslint-disable-next-line @typescript-eslint/no-unsafe-call
		new ContextualTooltip({
			element: tooltipElement,
			content: `Top ${percentage}% of Universities worldwide according to the Studyportals Meta Ranking`,
			position: 'top',
		});
	}

	public trackClick(): void {
		Tracking.searchCardClick(this);
	}

	public trackImpression(): void {
		if (!this.impressionTracked && getters.isNotTracked(this.presentationStudyCardInfo.value?.id)) {
			Tracking.searchCardImpression(this);
			this.impressionTracked = true;

			mutations.addTrackedStudyCardId(this.presentationStudyCardInfo.value?.id);
		}
	}

	public async getTuitionFee(): Promise<void | null> {
		if (!this.presentationStudyCardInfo.value.card.canShowTuitionFees?.()) {
			return null;
		}

		const localizedTuitionFee = await this.getLocalizedTuitionFee();
		if (!localizedTuitionFee) {
			return null;
		}

		const discountInformation = this.tuitionDiscountInformation.value;

		const amount = await this.getConvertedBasePrice(discountInformation, localizedTuitionFee);

		let discountedAmount: number | null = null;

		if (this.canSetDiscountedAmount(discountInformation, amount)) {
			discountedAmount = await this.getConvertedDiscountPrice(discountInformation, localizedTuitionFee);
		}

		const showDecimals = this.showDecimals(amount, localizedTuitionFee);

		this.tuitionFeeInfo.value = {
			amount: this.formatTuitionAmount(amount, showDecimals),
			discountedAmount: discountedAmount != null ? this.formatTuitionAmount(discountedAmount, showDecimals) : null,
			currency: this.userCurrency.value,
			unit: localizedTuitionFee.getUnit(),
		};
	}

	private canSetDiscountedAmount(discountInformation: DiscountInformation, amount: number): boolean {
		return (
			this.presentationStudyCardInfo.value.canShowIUDiscountInfo &&
			discountInformation != null &&
			discountInformation.percentage !== 0 &&
			discountInformation.priceInformation != null &&
			amount > 0
		);
	}

	private async getConvertedDiscountPrice(discountInformation: DiscountInformation, localizedTuitionFee: ITuitionFee): Promise<number> {
		const amount = discountInformation.priceInformation!.discountPricePerUnit;
		return await this.getConvertedAmount(amount, localizedTuitionFee);
	}

	private async getConvertedBasePrice(discountInformation: DiscountInformation, localizedTuitionFee: ITuitionFee): Promise<number> {
		const amount = this.getFinalBasePrice(discountInformation, localizedTuitionFee);
		return await this.getConvertedAmount(amount, localizedTuitionFee);
	}

	private async getConvertedAmount(amount: number, localizedTuitionFee: ITuitionFee): Promise<number> {
		return await globals.currencyConversionService.convert(amount, localizedTuitionFee.getCurrency(), this.userCurrency.value);
	}

	private getFinalBasePrice(discountInformation: DiscountInformation, localizedTuitionFee: ITuitionFee): number {
		return discountInformation.priceInformation ? discountInformation.priceInformation.basePricePerUnit : localizedTuitionFee.getAmount();
	}

	private showDecimals(amount: number, localizedTuitionFee: ITuitionFee): boolean {
		return localizedTuitionFee.getAmount() / amount >= 1000 && amount <= 10;
	}

	private async getLocalizedTuitionFee(): Promise<ITuitionFee | null> {
		let countryCode: null | string = null;
		try {
			countryCode = this.presentationStudyCardInfo.value.card.getCountryIsoCode?.();
		} catch {
			// Ignore error
		}

		const contents: LocalContent<ITuitionFee>[] = this.presentationStudyCardInfo.value.card.getTuitionFees?.().map((tuition) => {
			return new LocalContent(tuition.getTarget() as LocalizationTargetType, tuition, countryCode);
		});

		const localizedContent = await globals.localizedContentService.localizeContent(contents);
		if (!localizedContent) {
			return null;
		}

		return localizedContent.content;
	}

	private formatTuitionAmount(amount: number, showDecimals: boolean): string {
		if (amount <= 0) {
			return 'Free';
		}

		return amount.toLocaleString('nu', {
			style: 'decimal',
			minimumFractionDigits: 0,
			maximumFractionDigits: showDecimals ? 2 : 0,
		});
	}

	private plural(unit): string {
		return unit > 1 ? 's' : '';
	}

	private convertMonthlyDuration(inputMonths: number): string {
		const MONTHS_PER_YEAR = 12;
		const HALF_YEAR = 6;
		const years = Math.floor(inputMonths / MONTHS_PER_YEAR);
		const months = inputMonths % MONTHS_PER_YEAR;

		if (months === HALF_YEAR) {
			return `${years === 0 ? '' : years}½ ${TimeUnit.YEAR}${this.plural(years)}`;
		}

		if (years === 0) {
			return `${months} ${TimeUnit.MONTH}${this.plural(months)}`;
		}

		if (months === 0) {
			return `${years} ${TimeUnit.YEAR}${this.plural(years)}`;
		}

		return `${years} ${TimeUnit.YEAR}${this.plural(years)}, ${months} ${TimeUnit.MONTH}${this.plural(months)}`;
	}
}
